// src/pages/system/CustomCollections/details/index.jsx
import { useParams, useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import moment from 'moment'
import Swal from 'sweetalert2'
import { useTranslation } from 'react-i18next'

import i18n from '@/i18n'
import Loading from '@/widgets/Loading.jsx'
import CustomCollectionService from '@/services/CustomCollectionsServiceNew'
import { dispatchCollectionsChanged } from '@/lib/collectionsEvents'

const fmt = (d, pat = 'YYYY-MM-DD') => (d ? moment(d).format(pat) : '—')

export default function CustomCollectionDetails() {
	const { t } = useTranslation()
	const { id } = useParams()
	const navigate = useNavigate()

	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)
	const [collectionData, setCollectionData] = useState(null)
	const [deleting, setDeleting] = useState(false)

	// Load custom collection
	useEffect(() => {
		let alive = true
		;(async () => {
			setLoading(true)
			setError(null)
			const { data, error } = await CustomCollectionService.getCustomCollectionById(id)
			// console.log(`TCL -> index.jsx:28 -> CustomCollectionDetails -> data:`, data)
			if (!alive) return
			if (error) setError(error)
			else setCollectionData(data)
			setLoading(false)
		})()
		return () => {
			alive = false
		}
	}, [id])

	// Delete custom collection
	const handleDelete = async () => {
		if (!collectionData?._id) return
		const result = await Swal.fire({
			title: i18n.t('customCollections.details.deleteDialog.confirmTitle'),
			text: i18n.t('customCollections.details.deleteDialog.confirmText', { name: collectionData.name }),
			icon: 'warning',
			showCancelButton: true,
			confirmButtonColor: '#d33',
			cancelButtonColor: '#3085d6',
			confirmButtonText: i18n.t('customCollections.details.deleteDialog.confirmButton'),
			cancelButtonText: i18n.t('customCollections.details.deleteDialog.cancelButton'),
		})
		if (!result.isConfirmed) return

		setDeleting(true)
		try {
			const { success, error } = await CustomCollectionService.deleteCustomCollection(collectionData._id)
			if (!success) throw new Error(error || i18n.t('customCollections.details.messages.deleteFailed'))
			dispatchCollectionsChanged()

			await Swal.fire({
				title: i18n.t('customCollections.details.deleteDialog.deletedTitle'),
				text: i18n.t('customCollections.details.messages.deletedSuccess', { name: collectionData.name }),
				icon: 'success',
			})
			navigate('/custom-collections', {
				state: { message: i18n.t('customCollections.details.messages.deletedSuccess', { name: collectionData.name }) },
			})
		} catch (err) {
			await Swal.fire({
				title: i18n.t('customCollections.details.deleteDialog.errorTitle'),
				text: err.message || i18n.t('customCollections.details.messages.unexpectedDeleteError'),
				icon: 'error',
			})
		} finally {
			setDeleting(false)
		}
	}

	if (loading) return <Loading />
	if (error) return <div className="min-h-screen flex items-center justify-center">{t(error, { defaultValue: error })}</div>
	if (!collectionData) return <div className="min-h-screen flex items-center justify-center">{t('customCollections.details.noData')}</div>

	const collection = { ...collectionData }

	return (
		<>
			<div className="grid grid-cols-1 gap-6">
				{/* Collection details card */}
				<div className="rounded-lg bg-white shadow-sm">
					{/* Header */}
					<div className="flex w-full items-center justify-between space-x-6 p-6 py-4">
						<div className="flex-1 truncate">
							<div className="flex items-center space-x-3">
								<h2 className="truncate text-lg text-gray-900 font-semibold">{collection.name}</h2>
							</div>
						</div>
					</div>

					{/* Collection data */}
					<dl className="mt-2 px-6 text-sm/6 grid grid-cols-1 lg:grid-cols-2 gap-x-8">
						<div className="py-2 sm:flex">
							<dt className="font-medium text-gray-900 sm:w-32 sm:flex-none sm:pr-4">{t('customCollections.list.table.name')}</dt>
							<dd className="mt-1 text-gray-900 sm:mt-0">{collection.name}</dd>
						</div>
						
						<div className="py-2 sm:flex">
							<dt className="font-medium text-gray-900 sm:w-32 sm:flex-none sm:pr-4">{t('customCollections.details.labels.id')}</dt>
							<dd className="mt-1 text-gray-900 sm:mt-0 font-mono text-xs">{collection._id || collection.id}</dd>
						</div>

						<div className="py-2 sm:flex">
							<dt className="font-medium text-gray-900 sm:w-32 sm:flex-none sm:pr-4">{t('customCollections.list.table.visibleInMenu')}</dt>
							<dd className="mt-1 sm:mt-0">
								<span
									className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
										collection.visibleInMenu ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
									}`}
								>
									{collection.visibleInMenu ? t('customCollections.list.table.yes') : t('customCollections.list.table.no')}
								</span>
							</dd>
						</div>

						<div className="py-2 sm:flex">
							<dt className="font-medium text-gray-900 sm:w-32 sm:flex-none sm:pr-4">{t('customCollections.details.labels.showInParentCollection')}</dt>
							<dd className="mt-1 sm:mt-0">
								<span
									className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
										collection.showInParentCollection ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
									}`}
								>
									{collection.showInParentCollection ? t('customCollections.list.table.yes') : t('customCollections.list.table.no')}
								</span>
							</dd>
						</div>

						<div className="py-2 sm:flex">
							<dt className="font-medium text-gray-900 sm:w-32 sm:flex-none sm:pr-4">{t('customCollections.list.table.defaultSort')}</dt>
							<dd className="mt-1 text-gray-900 sm:mt-0">
								{collection.sortField ? (
									<span className="inline-flex items-center gap-2">
										<span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-800">
											{t('customCollections.list.table.sortField')}: {collection.sortField}
										</span>
										<span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-800">
											{t('customCollections.list.table.sortDirection')}: {t(`customCollections.list.sortDirections.${collection.sortDirection || 'desc'}`)}
										</span>
									</span>
								) : (
									<span className="text-gray-500">
										{t('customCollections.list.table.createdValue')} ({t('customCollections.list.sortDirections.desc')})
									</span>
								)}
							</dd>
						</div>

						{collection.created && (
							<div className="py-2 sm:flex">
								<dt className="font-medium text-gray-900 sm:w-32 sm:flex-none sm:pr-4">{t('customCollections.list.table.created')}</dt>
								<dd className="mt-1 text-gray-900 sm:mt-0">{fmt(collection.created, 'YYYY-MM-DD HH:mm')}</dd>
							</div>
						)}

						{collection.updated && (
							<div className="py-2 sm:flex">
								<dt className="font-medium text-gray-900 sm:w-32 sm:flex-none sm:pr-4">{t('customCollections.details.labels.updated')}</dt>
								<dd className="mt-1 text-gray-900 sm:mt-0">{fmt(collection.updated, 'YYYY-MM-DD HH:mm')}</dd>
							</div>
						)}
					</dl>

					{/* Fields section */}
					{collection.fields && collection.fields.length > 0 && (
						<div className="mt-6 px-6">
							<h3 className="text-base font-medium text-gray-900 mb-4">{t('customCollections.list.table.fields')}</h3>
							<div className="overflow-x-auto">
								<table className="min-w-full bg-white border border-gray-200 rounded-md">
									<thead className="bg-indigo-800">
										<tr>
											<th className="px-4 py-2 text-left text-sm font-medium text-white border-b border-indigo-700">{t('customCollections.list.table.name')}</th>
											<th className="px-4 py-2 text-left text-sm font-medium text-white border-b border-indigo-700">{t('customCollections.details.fieldsTable.type')}</th>
											<th className="px-4 py-2 text-center text-sm font-medium text-white border-b border-indigo-700">{t('customCollections.details.fieldsTable.required')}</th>
											<th className="px-4 py-2 text-center text-sm font-medium text-white border-b border-indigo-700">{t('customCollections.details.fieldsTable.filterPresent')}</th>
										</tr>
									</thead>
									<tbody>
										{collection.fields.map((field, index) => (
											<tr key={index} className="border-b border-gray-100">
												<td className="px-4 py-2 text-sm text-gray-900">{field.name}</td>
												<td className="px-4 py-2 text-sm text-gray-600 capitalize">
													{t(`customCollections.details.fieldTypes.${field.type || 'string'}`, { defaultValue: field.type || 'string' })}
												</td>
												<td className="px-4 py-2 text-center">
													<span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
														field.required 
															? 'bg-green-100 text-green-800' 
															: 'bg-gray-100 text-gray-800'
													}`}>
														{field.required ? t('customCollections.list.table.yes') : t('customCollections.list.table.no')}
													</span>
												</td>
												<td className="px-4 py-2 text-center">
													<span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
														field.filterpresent 
															? 'bg-blue-100 text-blue-800' 
															: 'bg-gray-100 text-gray-800'
													}`}>
														{field.filterpresent ? t('customCollections.list.table.yes') : t('customCollections.list.table.no')}
													</span>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>
					)}

					{/* Actions */}
					<div className="flex gap-3 px-6 pt-6 pb-6 justify-center">
						<button
							type="button"
							onClick={() => navigate(`/custom-collections/edit/${collection._id || collection.id}`)}
							className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md shadow-sm hover:bg-indigo-700">
							{t('customCollections.details.actions.edit')}
						</button>
						<button
							type="button"
							onClick={() => navigate(`/single-custom-collection/${collection._id || collection.id}/add`)}
							className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md shadow-sm hover:bg-green-700">
							{t('customCollections.details.actions.addNew')}
						</button>

						<button
							type="button"
							onClick={handleDelete}
							disabled={deleting}
							className={`inline-flex items-center px-4 py-2 text-sm font-medium text-white rounded-md shadow-sm ${
								deleting ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'
							}`}>
							{deleting ? t('customCollections.details.actions.deleting') : t('customCollections.details.actions.delete')}
						</button>
						<button
							type="button"
							onClick={() => navigate('/custom-collections')}
							className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50">
							{t('customCollections.details.actions.backToList')}
						</button>
					</div>
				</div>
			</div>
		</>
	)
}
